import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getAnalytics } from "../../analytics/events.ts";
import { listCourses, summaryBySlug, type CourseSummary } from "../../content/lookup.ts";
import { getReviewStore, reviewDumpSchema, type MasteryRecord } from "../../review/store.ts";
import { clearReportCardLog, loadReportCardLog } from "../../trainer/reportCardLog.ts";
import { soonCount, reviewTimeLabel } from "../../trainer/reviewQueue.ts";
import { getTrainer } from "../../trainer/session.ts";
import { useTrainer } from "../trainer/useTrainer.ts";
import { usePageMeta } from "../seo.ts";

/** The five review steps (Leitner boxes), from just seen to well known. */
const STEPS = [
  { name: "New or missed", note: "Back tomorrow" },
  { name: "Learning", note: "Right once" },
  { name: "Familiar", note: "Right twice in a row" },
  { name: "Solid", note: "Three in a row" },
  { name: "Mastered", note: "Four or more in a row" },
];

function stepCounts(cards: MasteryRecord[]): number[] {
  const steps = [0, 0, 0, 0, 0];
  for (const card of cards) steps[Math.min(5, Math.max(1, card.box)) - 1] += 1;
  return steps;
}

function plural(count: number, one: string, many = `${one}s`) {
  return `${count} ${count === 1 ? one : many}`;
}

type Loaded = { cards: MasteryRecord[]; due: MasteryRecord[]; now: number };

async function loadProgress(): Promise<Loaded> {
  const store = getReviewStore();
  const now = Date.now();
  const [cards, due] = await Promise.all([store.list(), store.due(now)]);
  return { cards, due, now };
}

export function ProgressPage() {
  usePageMeta({
    title: "Progress — The First 15 Moves",
    description: "Your saved positions, reviews and report cards. Saved on this device only.",
    path: "/progress",
  });
  const [data, setData] = useState<Loaded>({ cards: [], due: [], now: 0 });
  const [message, setMessage] = useState("");
  const [cardLog, setCardLog] = useState(() => loadReportCardLog());
  const fileRef = useRef<HTMLInputElement>(null);
  const snapshot = useTrainer();
  const courses = listCourses();

  useEffect(() => {
    let cancelled = false;
    void loadProgress().then((loaded) => {
      if (!cancelled) setData(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = async () => setData(await loadProgress());

  async function onExport() {
    const dump = await getReviewStore().exportDump(getAnalytics().list());
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "first-15-moves-progress.json";
    // In the page while it is clicked (Firefox), and kept alive a while:
    // Chrome on Android fetches a download after the click, so revoking the
    // URL at once can fail it.
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setMessage("Downloaded first-15-moves-progress.json.");
  }

  async function onImport(file: File) {
    try {
      const parsed = reviewDumpSchema.parse(JSON.parse(await file.text()));
      await getReviewStore().importDump(parsed);
      if (Array.isArray(parsed.events)) getAnalytics().replace(parsed.events as never);
      await reload();
      setMessage(`Imported ${plural(parsed.cards.length, "position")} from the backup.`);
    } catch {
      setMessage("That file is not a First 15 Moves progress backup.");
    }
  }

  function onReset() {
    if (!window.confirm("Reset all progress on this device? This cannot be undone.")) return;
    void (async () => {
      await getReviewStore().clear();
      getAnalytics().clear();
      clearReportCardLog();
      setCardLog([]);
      await reload();
      setMessage("Progress reset on this device.");
    })();
  }

  const { cards, due, now } = data;
  const steps = stepCounts(cards);
  const largestStep = Math.max(1, ...steps);
  const rows = courses.map((course) => ({
    course,
    saved: cards.filter((card) => card.openingId === course.openingId).length,
    due: due.filter((card) => card.openingId === course.openingId).length,
  }));
  const started = rows.filter((row) => row.saved > 0).length;
  const firstDue = rows.find((row) => row.due > 0)?.course;
  const soon = soonCount(cards, now);
  const resumable =
    snapshot.slug && getTrainer().canResume(snapshot.slug) ? summaryBySlug(snapshot.slug) : null;

  return (
    <section className="panel progress-panel" aria-labelledby="progress-heading">
      <nav className="progress-back" aria-label="Leave progress">
        {resumable ? (
          <Link className="opening-back-link" to={`/train/${resumable.slug}?mode=${snapshot.mode}`}>
            ← Back to game: {resumable.title} (move {snapshot.moveNumber})
          </Link>
        ) : (
          <Link className="opening-back-link" to="/openings">
            ← All openings
          </Link>
        )}
      </nav>

      <header className="progress-head">
        <p className="eyebrow">Saved on this device</p>
        <h2 id="progress-heading">Your progress</h2>
        <p className="section-sub">
          Studied positions return for review. Independent recall builds retention; a revealed
          answer is practice, and a miss begins relearning. Earlier progress is preserved. Nothing
          leaves this browser.
        </p>
      </header>

      <div className="progress-stats">
        <article className={`progress-stat${due.length ? " is-due" : ""}`}>
          <p className="progress-stat-label">Due for review</p>
          <p className="progress-stat-value">{due.length}</p>
          {firstDue ? (
            <Link className="btn-primary btn-sm" to={`/train/${firstDue.slug}?mode=review&mix=1`}>
              Review today’s mix
            </Link>
          ) : (
            <p className="progress-stat-note">
              {soon > 0
                ? `Next review ${reviewTimeLabel(Math.min(...cards.filter((card) => card.due > data.now).map((card) => card.due)), data.now)}.`
                : cards.length
                  ? "Nothing due right now."
                  : "Train a course to start."}
            </p>
          )}
        </article>
        <article className="progress-stat">
          <p className="progress-stat-label">Positions saved</p>
          <p className="progress-stat-value">{cards.length}</p>
          <p className="progress-stat-note">
            {started ? `Across ${plural(started, "course")}` : "None yet"}
          </p>
        </article>
        <article className="progress-stat">
          <p className="progress-stat-label">Retrieved independently</p>
          <p className="progress-stat-value">
            {cards.filter((card) => (card.independentReps || 0) > 0).length}
          </p>
          <p className="progress-stat-note">Verified by the new practice modes</p>
        </article>
        <article className="progress-stat">
          <p className="progress-stat-label">Report cards</p>
          <p className="progress-stat-value">{cardLog.length}</p>
          <p className="progress-stat-note">One every five moves</p>
        </article>
      </div>

      <section className="progress-section" aria-labelledby="progress-steps-heading">
        <h3 id="progress-steps-heading">Your review schedule</h3>
        <p className="progress-section-sub">
          Independent recall advances the schedule. Cues, reveals and repairs receive different
          credit. Older schedules are preserved, including grades earned before independent recall
          was tracked.
        </p>
        <ol className="progress-steps">
          {STEPS.map((step, index) => (
            <li key={step.name}>
              <span className="progress-step-name">
                <span className="progress-step-number">{index + 1}</span>
                {step.name}
              </span>
              <span className="progress-step-bar" aria-hidden="true">
                <span style={{ width: `${(steps[index] / largestStep) * 100}%` }} />
              </span>
              <strong className="progress-step-count">{steps[index]}</strong>
              <span className="progress-step-note">{step.note}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="progress-section" aria-labelledby="progress-courses-heading">
        <h3 id="progress-courses-heading">Your courses</h3>
        {(["white", "black"] as const).map((side) => (
          <div className="progress-course-group" key={side}>
            <h4>Playing {side === "white" ? "White" : "Black"}</h4>
            <ul className="progress-courses">
              {rows
                .filter((row) => row.course.side === side)
                .map((row) => (
                  <CourseRow key={row.course.slug} {...row} />
                ))}
            </ul>
          </div>
        ))}
      </section>

      <section className="progress-section" aria-labelledby="progress-cards-heading">
        <h3 id="progress-cards-heading">Recent report cards</h3>
        {cardLog.length === 0 ? (
          <p className="progress-section-sub">
            After every five moves of a course you get a report card on how they went. None yet on
            this device.
          </p>
        ) : (
          <ol className="progress-cards">
            {[...cardLog]
              .reverse()
              .slice(0, 12)
              .map((card) => (
                <li key={`${card.at}-${card.title}`}>
                  <span className="progress-card-word">{card.word}</span>
                  <span>
                    {card.openingTitle} · {card.title}
                  </span>
                  <span className="progress-card-meta">
                    {new Date(card.at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </li>
              ))}
          </ol>
        )}
      </section>

      <section className="progress-section progress-data" aria-labelledby="progress-data-heading">
        <h3 id="progress-data-heading">Your data</h3>
        <p className="progress-section-sub">
          Progress lives only in this browser. Export a backup to keep it safe or to move it to
          another browser or device, then import it there.
        </p>
        <div className="progress-data-actions">
          <button className="btn-ghost" type="button" onClick={() => void onExport()}>
            Export backup
          </button>
          <button className="btn-ghost" type="button" onClick={() => fileRef.current?.click()}>
            Import backup
          </button>
          <button className="btn-ghost btn-danger" type="button" onClick={onReset}>
            Reset progress
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onImport(file);
              event.target.value = "";
            }}
          />
        </div>
        {message ? (
          <p className="progress-message" role="status">
            {message}
          </p>
        ) : null}
      </section>
    </section>
  );
}

function CourseRow({ course, saved, due }: { course: CourseSummary; saved: number; due: number }) {
  return (
    <li>
      <span className="progress-course-name">
        <Link to={`/opening/${course.slug}`} aria-label={`${course.title} theory`}>
          {course.title}
        </Link>
        <span className="progress-course-meta">
          {saved ? plural(saved, "position") : "Not started"}
          {due ? ` · ${due} due` : ""}
        </span>
      </span>
      <span className="progress-course-actions">
        {due ? (
          <Link className="btn-primary btn-sm" to={`/train/${course.slug}?mode=review&mix=0`}>
            Review {due}
          </Link>
        ) : null}
        <Link
          className="btn-ghost btn-sm"
          to={`/train/${course.slug}?mode=learn`}
          aria-label={`Train ${course.title}`}
        >
          Train
        </Link>
      </span>
    </li>
  );
}
